import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { RefreshCw, X } from 'lucide-react'

const INPUT_CLASSES =
  'w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100'

interface ModalHeaderProps {
  title: string
  onClose: () => void
}

export function ModalHeader({ title, onClose }: ModalHeaderProps): ReactElement {
  return (
    <div className="mb-4 flex items-center justify-between">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      <button
        onClick={onClose}
        className="rounded p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

interface FieldProps {
  label: string
  required?: boolean
  children: ReactElement
}

export function Field({ label, required = false, children }: FieldProps): ReactElement {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
        {required && ' *'}
      </label>
      {children}
    </div>
  )
}

interface TextInputProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
  required?: boolean
}

export function TextInput({
  value,
  onChange,
  placeholder,
  required = false,
}: TextInputProps): ReactElement {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => {
        onChange(e.target.value)
      }}
      className={INPUT_CLASSES}
      placeholder={placeholder}
      required={required}
    />
  )
}

interface TextAreaProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
  rows: number
  required?: boolean
}

export function TextArea({
  value,
  onChange,
  placeholder,
  rows,
  required = false,
}: TextAreaProps): ReactElement {
  return (
    <textarea
      value={value}
      onChange={(e) => {
        onChange(e.target.value)
      }}
      rows={rows}
      className={INPUT_CLASSES}
      placeholder={placeholder}
      required={required}
    />
  )
}

interface CategorySelectorProps {
  category: string
  onCategoryChange: (value: string) => void
  newCategory: string
  onNewCategoryChange: (value: string) => void
  categories: string[]
  t: TFunction<'settings'>
}

export function CategorySelector({
  category,
  onCategoryChange,
  newCategory,
  onNewCategoryChange,
  categories,
  t,
}: CategorySelectorProps): ReactElement {
  return (
    <Field label={t('templates.category')}>
      <div>
        <div className="flex gap-2">
          <select
            value={category}
            onChange={(e) => {
              onCategoryChange(e.target.value)
              onNewCategoryChange('')
            }}
            className={`flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100`}
          >
            <option value="">{t('templates.selectCategory')}</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-2">
          <input
            type="text"
            value={newCategory}
            onChange={(e) => {
              onNewCategoryChange(e.target.value)
              onCategoryChange('')
            }}
            className={INPUT_CLASSES}
            placeholder={t('templates.newCategory')}
          />
        </div>
      </div>
    </Field>
  )
}

interface FormFooterProps {
  onClose: () => void
  submitting: boolean
  canSubmit: boolean
  saveLabel: string
  cancelLabel: string
}

export function FormFooter({
  onClose,
  submitting,
  canSubmit,
  saveLabel,
  cancelLabel,
}: FormFooterProps): ReactElement {
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {cancelLabel}
      </button>
      <button
        type="submit"
        disabled={submitting || !canSubmit}
        className="inline-flex items-center gap-2 rounded-lg bg-fuchsia-600 px-4 py-2 text-sm font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50"
      >
        {submitting && <RefreshCw className="h-4 w-4 animate-spin" />}
        {saveLabel}
      </button>
    </div>
  )
}

interface ErrorBannerProps {
  message: string
}

export function ErrorBanner({ message }: ErrorBannerProps): ReactElement {
  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50">
      <p className="text-sm text-red-700 dark:text-red-400">{message}</p>
    </div>
  )
}
